import React from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, LogIn, Inbox, ImageIcon, Sparkles, Briefcase,
  Settings, Download, FileText, AlertCircle, CheckCircle2, Lightbulb, Users,
} from "lucide-react";
import { API } from "../../lib/api";

const Section = ({ icon: Icon, num, title, children }) => (
  <section className="bg-white border border-gray-200 p-6 lg:p-8">
    <header className="flex items-start gap-4">
      <div className="w-10 h-10 bg-[#0A192F] flex items-center justify-center flex-shrink-0">
        <Icon className="text-[#D4AF37]" size={18} />
      </div>
      <div>
        <div className="font-display text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">
          Step {num}
        </div>
        <h2 className="mt-1 font-display font-bold text-xl text-[#0A192F]">{title}</h2>
      </div>
    </header>
    <div className="mt-5 space-y-3 text-sm text-[#0A192F]/80 leading-relaxed">{children}</div>
  </section>
);

const Tip = ({ children, tone = "tip" }) => {
  const tones = {
    tip: { color: "text-[#0A192F]", bg: "bg-[#FFF9E6]", border: "border-[#D4AF37]", icon: Lightbulb },
    warn: { color: "text-red-800", bg: "bg-red-50", border: "border-red-300", icon: AlertCircle },
    ok: { color: "text-green-800", bg: "bg-green-50", border: "border-green-300", icon: CheckCircle2 },
  };
  const t = tones[tone];
  const Icon = t.icon;
  return (
    <div className={`flex items-start gap-3 ${t.bg} border-l-4 ${t.border} px-4 py-3 ${t.color}`}>
      <Icon size={16} className="mt-0.5 flex-shrink-0" />
      <div>{children}</div>
    </div>
  );
};

const AdminHelp = () => (
  <div data-testid="admin-help-page" className="space-y-8">
    <header className="flex items-end justify-between flex-wrap gap-4">
      <div>
        <div className="font-display text-[11px] uppercase tracking-[0.3em] text-[#0A192F]/60">
          Tutorial
        </div>
        <h1 className="mt-2 font-display font-black text-4xl text-[#0A192F] tracking-tighter">
          How to Edit the Site
        </h1>
        <p className="mt-3 text-[#0A192F]/70 max-w-2xl">
          Welcome! This guide walks you through every section of the admin so you can update the
          website yourself. Total time to read: ~5 minutes.
        </p>
      </div>
      <div className="flex flex-wrap gap-3">
        <a
          href={`${API}/admin/help/guide.pdf`}
          data-testid="admin-help-download-pdf"
          className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-5 py-3 font-display text-xs uppercase tracking-[0.25em] font-bold hover:bg-[#D4AF37] hover:text-[#0A192F] transition-colors"
        >
          <Download size={14} /> Download Guide (PDF)
        </a>
        <a
          href="/cm-lindley-cms-guide.md"
          download
          data-testid="admin-help-download"
          className="inline-flex items-center gap-2 border border-[#0A192F] text-[#0A192F] px-5 py-3 font-display text-xs uppercase tracking-[0.25em] font-bold hover:bg-[#0A192F] hover:text-white transition-colors"
        >
          <FileText size={14} /> Download (Markdown)
        </a>
      </div>
    </header>

    <Section num="1" title="Signing In" icon={LogIn}>
      <p>
        The admin lives at <code className="bg-gray-100 px-1.5 py-0.5">/admin/login</code>.
        Use the email and password you were given.
      </p>
      <Tip>Bookmark <code>yourdomain.com/admin/login</code> for fast access. The link is intentionally not on the public site.</Tip>
      <p>
        Your session stays signed in for 8 hours. After that, sign in again. Click{" "}
        <strong>Sign Out</strong> at the bottom of the sidebar (or top-right on mobile) whenever you finish.
      </p>
    </Section>

    <Section num="2" title="Managing Users (Super Admin only)" icon={Users}>
      <p>
        Click <Link to="/admin/users" className="underline">Users</Link> in the sidebar. This page is
        only visible to <strong>Super Admins</strong>.
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>+ Add User</strong> - choose <em>Set it now</em> to type a temporary password, or <em>Send invite</em> to email a 72-hour activation link (we also show the link so you can copy/paste it).</li>
        <li><strong>Role dropdown</strong> on each row - Admin (content + leads + media) or Super Admin (everything, including this Users page).</li>
        <li><strong>Reset password</strong> - sets a new temp password; share it with the user out-of-band.</li>
        <li><strong>Deactivate / Reactivate</strong> - instantly blocks/unblocks login without deleting history.</li>
        <li><strong>Delete</strong> - permanent. The last active Super Admin can't be deleted or demoted; promote someone else first.</li>
      </ul>
      <Tip>Want to add a new admin? Quickest path: <em>+ Add User → Send invite</em>. They click the email link, set their own password, and land in the admin.</Tip>
    </Section>

    <Section num="3" title="Reading Leads" icon={Inbox}>
      <p>
        Every contact-form submission appears in <Link to="/admin/leads" className="underline">Leads</Link>. Click any row to read the full message,
        change its status (<em>New → Contacted → Won / Lost</em>), or delete it.
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Search box</strong>: find a lead by name, company, email, or message keywords.</li>
        <li><strong>Status filter</strong>: see only New, Contacted, Won, or Lost leads.</li>
        <li><strong>Reply by Email</strong>: opens your default mail app pre-addressed to the lead.</li>
        <li><strong>Export CSV</strong>: download every lead as a spreadsheet for your CRM.</li>
      </ul>
      <Tip tone="ok">Leads also email you at <strong>info@cmlindleyllc.com</strong> the moment they come in (once the Resend API key is configured).</Tip>
    </Section>

    <Section num="4" title="The Media Library" icon={ImageIcon}>
      <p>
        Go to <Link to="/admin/media" className="underline">Media Library</Link>. This works like WordPress's media library: you upload images
        and videos once, then re-use them anywhere on the site.
      </p>
      <ol className="list-decimal pl-6 space-y-1">
        <li>Click <strong>Upload Files</strong> in the top right.</li>
        <li>Pick one or multiple images / mp4s (max 25 MB per file).</li>
        <li>Each file gets a permanent URL. Click <strong>Copy URL</strong> to grab it.</li>
      </ol>
      <Tip>Most editors also have a <strong>Browse</strong> button - click it to pick from your existing library without copy-pasting URLs.</Tip>
    </Section>

    <Section num="5" title="Editing the Home Hero" icon={Sparkles}>
      <p>
        Open <Link to="/admin/hero" className="underline">Hero (Home)</Link>. This is the big top section of the home page.
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Headline Line 1 / Line 2</strong> - Line 2 is colored gold.</li>
        <li><strong>Sub-headline</strong> - the descriptive paragraph below the headline.</li>
        <li><strong>Hero Video URL</strong> - keep <code>/media/hero.mp4</code>, or upload a new video in Media Library and paste its URL here.</li>
        <li><strong>Stats 1–4</strong> - the row of 4 numbers across the bottom of the hero.</li>
      </ul>
      <p>Click <strong>Save Changes</strong> at the bottom. Refresh the home page in a new tab to see the result.</p>
    </Section>

    <Section num="6" title="Managing Services" icon={Briefcase}>
      <p>
        <Link to="/admin/services" className="underline">Services</Link> controls the Lumber, Steel, and 4 "Secondary Services" tiles.
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Type = Lumber / Steel (Featured)</strong> - these become the two big half-page tiles on the Services page. Each has an image and a bullet-list of features.</li>
        <li><strong>Type = Secondary</strong> - appears in the 4-icon grid below. Pick an icon from the dropdown.</li>
        <li><strong>Features</strong> - type one bullet per line. Order matters.</li>
        <li><strong>Sort Order</strong> - lower numbers appear first within their category.</li>
      </ul>
    </Section>

    <Section num="7" title="Managing the Gallery" icon={ImageIcon}>
      <p>
        <Link to="/admin/gallery" className="underline">Gallery</Link> controls the Photo Gallery page (and the 4-image preview on the home page).
      </p>
      <ol className="list-decimal pl-6 space-y-1">
        <li>Click <strong>Add Photo</strong>.</li>
        <li>Paste an image URL or click <strong>Browse</strong> to pick from Media Library.</li>
        <li>Add a label and choose a category (Lumber, Steel, Framing, Delivery, Job Site, General).</li>
        <li>Use the <strong>↑ ↓ arrows</strong> on each card to reorder photos.</li>
      </ol>
      <Tip tone="warn">Categories are case-sensitive. Always pick from the dropdown - don't free-type.</Tip>
    </Section>

    <Section num="8" title="About Page & Site Settings" icon={FileText}>
      <p>
        <Link to="/admin/about" className="underline">About Page</Link> edits the story text and the image on the About page.
        Use two blank lines to start a new paragraph.
      </p>
      <p>
        <Link to="/admin/settings" className="underline">Site Settings</Link> controls the email, phone, address, hours, and footer
        blurb - everything that appears in the footer and on the Contact page.
      </p>
      <Tip>After editing Settings, all 5 public pages update instantly. No deploy needed.</Tip>
    </Section>

    <Section num="8" title="If something goes wrong" icon={AlertCircle}>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Forgot your password?</strong> Ask your developer to reset it via the server admin.</li>
        <li><strong>"Save Changes" doesn't work?</strong> Refresh the page - your session may have expired. Sign in again.</li>
        <li><strong>An image isn't showing on the public site?</strong> Make sure it's set to <em>not deleted</em> in Media Library, and double-check the URL is correct.</li>
        <li><strong>Need to add new categories or fields?</strong> Reach out to your developer - they're added in code.</li>
      </ul>
    </Section>

    <section className="bg-[#0A192F] text-white p-6 lg:p-8">
      <h2 className="font-display font-bold text-xl text-white">That's it.</h2>
      <p className="mt-3 text-white/70 max-w-2xl text-sm">
        You can now manage 95% of what's on the site without touching code. If you ever need a new
        page or feature, send your developer a description and they'll add it.
      </p>
    </section>
  </div>
);

export default AdminHelp;
